/**
 * Room subscription helpers for the Hostesa realtime gateway.
 *
 * The gateway organises connections into two room types:
 *   - Hotel room  (`hotel:{hotelId}`) — receives hotel-scoped events (call
 *     status, handoff, reservation, booking, task, knowledge gaps).
 *   - Call room   (`call:{callId}`)   — receives high-frequency call events
 *     (transcript segments) for a single active call.
 *
 * Both helpers fire-and-forget the emit; callers are responsible for
 * cleanup on unmount (see `useHotelRoom` / `useCallRoom` hooks).
 */
import type { RealtimeSocket } from './socket';

// ---------------------------------------------------------------------------
// Ack shape from the gateway
// ---------------------------------------------------------------------------

interface SubscribeAck {
  ok: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Hotel room
// ---------------------------------------------------------------------------

/**
 * Subscribes the socket to the hotel room for `hotelId`. Should be called
 * when a hotel-scoped route mounts.
 *
 * Returns a cleanup function that unsubscribes.
 */
export function subscribeHotelRoom(
  socket: RealtimeSocket,
  hotelId: string,
  onError?: (reason: string) => void,
): () => void {
  socket.emit('hotel.subscribe', { hotelId }, (ack: SubscribeAck) => {
    if (!ack.ok) {
      const reason = ack.error ?? 'hotel.subscribe rejected by server';
      console.warn('[realtime] hotel.subscribe failed', { hotelId, reason });
      onError?.(reason);
    }
  });

  return () => {
    unsubscribeHotelRoom(socket, hotelId);
  };
}

/**
 * Unsubscribes the socket from the hotel room. Called on unmount or when the
 * hotel scope changes.
 */
export function unsubscribeHotelRoom(socket: RealtimeSocket, hotelId: string): void {
  socket.emit('hotel.unsubscribe', { hotelId });
}

// ---------------------------------------------------------------------------
// Call room
// ---------------------------------------------------------------------------

/**
 * Subscribes the socket to the call room for `callId`. Should be called when
 * a call detail view or live-call side panel opens.
 *
 * Returns a cleanup function that unsubscribes.
 */
export function subscribeCallRoom(
  socket: RealtimeSocket,
  hotelId: string,
  callId: string,
  onError?: (reason: string) => void,
): () => void {
  socket.emit('call.subscribe', { hotelId, callId }, (ack: SubscribeAck) => {
    if (!ack.ok) {
      const reason = ack.error ?? 'call.subscribe rejected by server';
      console.warn('[realtime] call.subscribe failed', { callId, reason });
      onError?.(reason);
    }
  });

  return () => {
    unsubscribeCallRoom(socket, hotelId, callId);
  };
}

/**
 * Unsubscribes the socket from the call room. Called on unmount or when the
 * call detail panel closes.
 */
export function unsubscribeCallRoom(socket: RealtimeSocket, hotelId: string, callId: string): void {
  socket.emit('call.unsubscribe', { hotelId, callId });
}
